import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MailCreatedDTO, MailFileDto, MailMeSearchPaginationDTO, MailWithFilesCreatedDTO } from '../interfaces';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { MailsMeSearchParams } from 'src/app/shared/models/mails/mails-me-search';
import { MailInsert } from 'src/app/shared/models/iCourrier/post/mail-insert';
import { HistoryDTO } from 'src/app/shared/models/iCourrier/history';
import { AccessHistoryDTO } from 'src/app/shared/models/iCourrier/access-history';
import { MailMeCountDTO, MailMeCountFilesDTO } from 'src/app/shared/models/mails/mail-me-count';
import { MailBasicInfosDTO, MailCountsDTO, MailDTO, MailFullInfosDTO } from '../interfaces/mails/mail-dto.interface';
import { ScanSearchMailsDTO } from '../interfaces/scans/scan-search-mail-dto.interface';
import { ScanSearchMailsParams } from 'src/app/shared/models/iCourrier/scans/scan-search-mails';
import { BadRoutingDTO } from '../interfaces/bad-routing/bad-routing-dto.interface';
import { BadRoutingParams } from 'src/app/shared/models/mails/bad-routing';
import { MailsEcheancierParams } from 'src/app/shared/models/mails/mails-echeancier';
import { MailEcheancierSearchResultPaginatedDTO } from '../interfaces/mails/mail-echeancier-dto.interface';
import { MailDuplicateParams } from 'src/app/shared/models/mails/mail-duplicate';
import { EmailInput } from 'src/app/shared/models/iCourrier/email/email-input';
import { environment } from 'src/environments/environment';
import { setServiceDescription } from 'src/app/utils/set-service-description.utils';

@Injectable({
  providedIn: 'root',
})
export class MailsService {
  private url: string;

  constructor(private http: HttpClient) {
    const config = environment;
    this.url = config.apiICourrierBaseUrl;
  }

  //#region Access to mail
  public canAccessToMail = new BehaviorSubject<number>(0);
  public canAccessToMail$ = this.canAccessToMail.asObservable();
  public getCanAccessToMail() {
    return this.canAccessToMail.asObservable();
  }
  public setCanAccessToMail(value: number) {
    return this.canAccessToMail.next(value);
  }

  //#endregion

  //#region Mail search / get
  public fetchMailsById(id: number): Observable<MailDTO> {
    return this.http.get<MailDTO>(`${this.url}/Mails/${id}`);
  }
  public fetchMailCounts(id: number): Observable<MailCountsDTO> {
    return this.http.get<MailCountsDTO>(`${this.url}/Mails/${id}/Counts`);
  }

  public fetchMailDetails(mailId: number): Observable<MailFullInfosDTO> {
    return this.http.get<MailFullInfosDTO>(`${this.url}/Mails/${mailId}/FullInfos`);
  }
  public fetchMailBasic(mailId: number): Observable<MailBasicInfosDTO> {
    return this.http.get<MailBasicInfosDTO>(`${this.url}/Mails/${mailId}/BasicInfos`);
  }
  //#endregion

  //#region Mail add
  public addMail(mail: MailInsert, skipWarns?: boolean): Observable<MailCreatedDTO> {
    let params = new HttpParams();
    if (skipWarns) {
      params = new HttpParams().set('skipWarns', skipWarns.toString());
    }
    return this.http.post<MailCreatedDTO>(`${this.url}/Mails?${params}`, mail);
  }

  public addMailFull(document): Observable<MailWithFilesCreatedDTO> {
    const body = new FormData();
    for (const property in document) {
      if (Object.prototype.hasOwnProperty.call(document, property)) {
        if (property === 'Files') {
          document[property].map((item) => {
            body.append('Files', item);
          });
        }
        if (property === 'autoDistributionServices') {
          body.append('autoDistributionServices', JSON.stringify(document[property]));
        }

        if (property === 'autoDistributionbUsers') {
          body.append('autoDistributionbUsers', JSON.stringify(document[property]));
        }
        if (property === 'NatureCustomDatas') {
          // This could be made fancier but it explains how to fix the problem
          document['NatureCustomDatas'].forEach((obj, index) => {
            Object.keys(obj).forEach((key) => {
              if (obj[key] !== null) {
                body.append(`NatureCustomDatas[${index}].${key}`, obj[key]);
              }
            });
          });
        }
        if (property === 'lien') {
          body.append('lien.linkCourrierId', document[property].linkCourrierId);
          body.append('lien.typeLiaison', document[property].typeLiaison);
        }
        if (
          property !== 'Files' &&
          property !== 'autoDistributionServices' &&
          property !== 'autoDistributionbUsers' &&
          property !== 'NatureCustomDatas' &&
          property !== 'lien'
        ) {
          body.append(property, document[property]);
        }
      }
    }
    return this.http.post<MailWithFilesCreatedDTO>(`${this.url}/Mails/Full?skipWarns=true`, body);
  }

  //#endregion

  //#region Mail Update
  public updateMail(id: number, mail: MailInsert, skipWarns?: boolean): Observable<MailDTO> {
    let params = new HttpParams();
    if (skipWarns) {
      params = new HttpParams().set('skipWarns', skipWarns.toString());
    }
    return this.http.put<MailDTO>(`${this.url}/Mails/${id}?${params}`, mail);
  }

  //#endregion

  //#region Mail Duplicate
  postDuplicate(courrierID: number, data: MailDuplicateParams): Observable<MailWithFilesCreatedDTO> {
    const body = new FormData();
    for (const property in data) {
      if (Object.prototype.hasOwnProperty.call(data, property)) {
        // if (data[property])
        if (property === 'Files') {
          data[property].map((item) => {
            body.append('Files', item);
          });
        } else if (data[property] !== null && data[property] !== '' && data[property] !== undefined) {
          body.append(property, data[property]);
        }
      }
    }
    return this.http.post<MailWithFilesCreatedDTO>(`${this.url}/Mails/${courrierID}/Duplicate`, body);
  }
  //#endregion

  //#region Mail Delete
  deleteCourrier(id: number, deleted: boolean): Observable<void> {
    return this.http.delete<void>(`${this.url}/Mails/${id}?deleted=${deleted}`);
  }
  //#endregion

  //#region Scan Mails search

  public searchMailsToAttach(data?: ScanSearchMailsParams): Observable<ScanSearchMailsDTO[]> {
    let params = new HttpParams();
    if (data) {
      Object.keys(data).forEach((key) => {
        params = params.set(key, data[key]);
      });
    }
    return this.http.get<ScanSearchMailsDTO[]>(`${this.url}/ScanLots/SearchMailsToAttach?${params}`);
  }
  //#endregion

  //#region Mails Me Search
  public getAllMails(data?: MailsMeSearchParams, sizeChoice?: number): Observable<MailMeSearchPaginationDTO> {
    const dataFiltered = this.clearDataParamsNatureCustomFieldsForGetAllMails(data);
    let params = new HttpParams();
    if (dataFiltered) {
      Object.keys(dataFiltered).forEach((key) => {
        if (key === 'NatureCustomFieldsSearch') {
          dataFiltered['NatureCustomFieldsSearch'].forEach((obj, index) => {
            Object.keys(obj).forEach((key) => {
              if (obj[key] !== null) {
                if (key === 'dropdownValueItemIds') {
                  params = params.set(`NatureCustomFieldsSearch[${index}].DropdownValueItemIds`, obj[key].join(','));
                } else {
                  params = params.set(`NatureCustomFieldsSearch[${index}].${key}`, obj[key]);
                }
              }
            });
          });
        } else {
          params = params.set(key, dataFiltered[key]);
        }
      });
    }
    if (sizeChoice) {
      return this.http
        .get<MailMeSearchPaginationDTO>(`${this.url}/Mails/Me/Search?${params}`, { params: { pageSize: sizeChoice } })
        .pipe(
          map((mailMeSearchPaginationDTO: MailMeSearchPaginationDTO) => ({
            ...mailMeSearchPaginationDTO,
            items: (mailMeSearchPaginationDTO.items ?? []).map((mail) => ({
              ...mail,
              serviceProprietaire: { ...mail.serviceProprietaire, description: setServiceDescription(mail.serviceProprietaire) },
            })),
          }))
        );
    } else {
      return this.http
        .get<MailMeSearchPaginationDTO>(`${this.url}/Mails/Me/Search?${params}`)
        .pipe(
          map((mailMeSearchPaginationDTO: MailMeSearchPaginationDTO) => ({
            ...mailMeSearchPaginationDTO,
            items: (mailMeSearchPaginationDTO.items ?? []).map((mail) => ({
              ...mail,
              serviceProprietaire: { ...mail.serviceProprietaire, description: setServiceDescription(mail.serviceProprietaire) },
            })),
          }))
        );
    }
  }
  private clearDataParamsNatureCustomFieldsForGetAllMails(data) {
    if (data?.NatureCustomFieldsSearch) {
      data.NatureCustomFieldsSearch = data.NatureCustomFieldsSearch.filter(
        (natureCustomField) => natureCustomField.type !== 'dropdown' || this.getDropdownValueItemIds(natureCustomField).length > 0
      );
      data.NatureCustomFieldsSearch.map((natureCustomField) => {
        if (natureCustomField.type === 'dropdown') {
          natureCustomField.dropdownValueItemIds = this.getDropdownValueItemIds(natureCustomField);
        }
        natureCustomField.type ? delete natureCustomField.type : null;
        natureCustomField.label ? delete natureCustomField.label : null;
        natureCustomField.listValueItemId ? delete natureCustomField.listValueItemId : null;
        natureCustomField.textareaValue ? delete natureCustomField.textareaValue : null;
        return natureCustomField;
      });
      return data;
    } else return data;
  }

  private getDropdownValueItemIds(natureCustomField: { dropdownValueItemIds?: number[]; listValueItemId?: number | number[] }): number[] {
    const selectedIds = natureCustomField.dropdownValueItemIds ?? natureCustomField.listValueItemId ?? [];
    return [].concat(selectedIds).filter((id) => id > 0);
  }
  //#endregion

  //#region Mail services
  public deleteMailsServices(
    id: number,
    data: {
      type: string;
      destinataires: number[];
    }
  ): Observable<void> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: data,
    };
    return this.http.delete<void>(`${this.url}/Mails/${id}/Services`, options);
  }

  //#endregion

  //#region Mails count
  /**
   * @param tags Les tags à filtrer. Available values : All, Entrants, Sortants, Internes, Lus, NonLus, Epingles, PourInfo, PourAction, AValider, EntrantsAValider, SortantsAValider, ACompleter, ADistribuer, APrendreEnCharge, PrisEnCharge, NonPrisEnCharge, MalRoutes, AClasser
   */
  public getMailsCount(args: string): Observable<MailMeCountDTO> {
    return this.http.get<MailMeCountDTO>(`${this.url}/Mails/Me/Count?tags=${args}`);
  }

  /**
   * @param tags Les tags à filtrer. Available values : DocAValider, DocASigner
   */
  public getMailsCountFiles(tags: string, fromCache = true): Observable<MailMeCountFilesDTO> {
    return this.http.get<MailMeCountFilesDTO>(`${this.url}/Mails/Me/CountFiles?tags=${tags}&fromCache=${fromCache}`);
  }

  //#endregion

  //#region Mail Boite Archive
  public patchBoiteArchive(id: number, data: { boiteArchive: string }): Observable<void> {
    return this.http.patch<void>(`${this.url}/Mails/${id}/BoiteArchive`, data);
  }

  //#endregion

  //#region Mail history
  public getHistory(id: number): Observable<HistoryDTO[]> {
    return this.http.get<HistoryDTO[]>(`${this.url}/Mails/${id}/History`);
  }
  public getAccessHistory(id: number): Observable<AccessHistoryDTO[]> {
    return this.http.get<AccessHistoryDTO[]>(`${this.url}/Mails/${id}/AccessHistory`);
  }

  //#endregion

  //#region Mail read
  public markAsReaded(data: { mailIds: number[] }): Observable<void> {
    return this.http.post<void>(`${this.url}/Mails/MarkAsReaded`, data);
  }

  //#endregion

  //#region Mail validate
  public validateMail(data: { mailIds: number[] }): Observable<number> {
    return this.http.put<number>(`${this.url}/Mails/Validate`, data);
  }

  //#endregion

  //#region Mail mark as distributed
  public markAsDistributed(data: { mailIds: number[] }): Observable<number> {
    return this.http.put<number>(`${this.url}/Mails/MarkAsDistributed`, data);
  }

  //#endregion

  //#region Mail status
  public patchMailStatus(id: number, statusID: number): Observable<void> {
    return this.http.patch<void>(`${this.url}/Mails/${id}/Status?nextStatusId=${statusID}`, null);
  }

  //#endregion

  //#region Pin
  public pinMail(courrierID: number, pinned: boolean): Observable<void> {
    return this.http.post<void>(`${this.url}/Mails/${courrierID}/Pin?pinned=${pinned}`, {});
  }
  //#endregion

  //#region Mauvais routage
  public getMauvaisRoutageService(id: number): Observable<BadRoutingDTO> {
    return this.http.get<BadRoutingDTO>(`${this.url}/Mails/${id}/MauvaisRoutageService`);
  }

  public putMauvaisRoutageService(id: number, data: BadRoutingParams): Observable<number> {
    return this.http.put<number>(`${this.url}/Mails/${id}/MauvaisRoutageService`, data);
  }
  //#endregion

  //#region Echeancier

  public getEcheancier(datas?: MailsEcheancierParams): Observable<MailEcheancierSearchResultPaginatedDTO> {
    if (datas) {
      let params = new HttpParams();
      Object.keys(datas).forEach((key) => (params = params.set(key, datas[key])));
      return this.http.get<MailEcheancierSearchResultPaginatedDTO>(`${this.url}/Mails/Echeancier/Search?${params}`);
    } else {
      return this.http.get<MailEcheancierSearchResultPaginatedDTO>(`${this.url}/Mails/Echeancier/Search`);
    }
  }
  //#endregion

  //#region reply
  public sendEmailReply(id: number, emailInput: EmailInput): Observable<MailFileDto> {
    const body = new FormData();

    for (const property in emailInput) {
      if (Object.prototype.hasOwnProperty.call(emailInput, property)) {
        if (property === 'staticAttachments') {
          for (let i = 0; i < emailInput[property].length; i++) {
            body.append('staticAttachements', emailInput[property][i]);
          }
        }
      }
    }
    body.append('jsonData', JSON.stringify(emailInput.jsonData));
    return this.http.post<MailFileDto>(`${this.url}/Mails/${id}/Replies/SendEmail`, body);
  }
  //#endregion
}
